import { useState } from "react";
import { X, Copy, Check, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

// Simple X (Twitter) glyph (lucide no longer ships Twitter; use inline svg)
const XLogo = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppLogo = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.9 11.9 0 0 0 12.04 0C5.46 0 .13 5.33.13 11.91c0 2.1.55 4.14 1.6 5.95L0 24l6.3-1.65a11.9 11.9 0 0 0 5.74 1.46h.01c6.58 0 11.91-5.33 11.92-11.91a11.86 11.86 0 0 0-3.45-8.42zM12.05 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.74.98 1-3.65-.23-.37A9.86 9.86 0 1 1 21.9 11.9c0 5.47-4.45 9.9-9.85 9.9zm5.42-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.76.97-.93 1.17-.17.2-.34.22-.63.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.6.14-.14.3-.34.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.5-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.47s1.07 2.86 1.22 3.06c.15.2 2.1 3.2 5.08 4.49.71.3 1.27.49 1.7.63.72.23 1.37.2 1.88.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

const TelegramLogo = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M11.944 0C5.344 0 0 5.343 0 11.944c0 6.6 5.343 11.944 11.944 11.944 6.6 0 11.944-5.344 11.944-11.944C23.888 5.344 18.544 0 11.944 0zm5.47 8.187-1.825 8.6c-.138.624-.5.78-1.015.485l-2.8-2.064-1.351 1.3c-.15.15-.274.274-.562.274l.2-2.85 5.189-4.69c.226-.2-.049-.313-.35-.113l-6.414 4.04-2.76-.863c-.6-.188-.613-.6.126-.887l10.787-4.157c.5-.188.937.112.775.937z" />
  </svg>
);

export function ShareModal({ open, onClose, title, url, text }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const shareUrl = url;
  const shareText = text || title || "Check this out";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const openUrl = (u) => {
    window.open(u, "_blank", "noopener,noreferrer");
  };

  const tryNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || "", text: shareText, url: shareUrl });
        onClose();
      } catch { /* user cancelled */ }
    }
  };

  const wa = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;
  const tg = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
  const tw = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
  const mail = `mailto:?subject=${encodeURIComponent(title || "Shared listing")}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;

  return (
    <div
      data-testid="share-modal-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
    >
      <div
        data-testid="share-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-md border border-border shadow-xl overflow-hidden"
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">Share</div>
            <div className="font-display text-lg font-bold truncate max-w-[260px]">{title || "This listing"}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-modal-close"
            aria-label="Close"
            className="h-9 w-9 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-4 gap-3">
          <ShareOption testId="share-whatsapp" label="WhatsApp" onClick={() => openUrl(wa)} color="bg-[#25D366] text-white">
            <WhatsAppLogo />
          </ShareOption>
          <ShareOption testId="share-telegram" label="Telegram" onClick={() => openUrl(tg)} color="bg-[#229ED9] text-white">
            <TelegramLogo />
          </ShareOption>
          <ShareOption testId="share-x" label="X" onClick={() => openUrl(tw)} color="bg-foreground text-background">
            <XLogo />
          </ShareOption>
          <ShareOption testId="share-email" label="Email" onClick={() => openUrl(mail)} color="bg-secondary text-foreground border border-border">
            <Mail className="h-5 w-5" />
          </ShareOption>
        </div>

        <div className="px-5 pb-5">
          <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/50 p-2 pl-3">
            <div className="font-mono-brand text-xs truncate flex-1" data-testid="share-url">{shareUrl}</div>
            <button
              type="button"
              onClick={copy}
              data-testid="share-copy-link"
              className="inline-flex items-center gap-1.5 rounded-md bg-foreground text-background h-8 px-3 text-xs font-semibold hover:bg-primary transition-colors shrink-0"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>

          {typeof navigator !== "undefined" && navigator.share && (
            <button
              type="button"
              onClick={tryNative}
              data-testid="share-native"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border h-10 text-sm font-semibold hover:border-foreground/30 transition-colors"
            >
              <Send className="h-4 w-4" />
              More options…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ShareOption({ children, label, onClick, color, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex flex-col items-center gap-2 group"
    >
      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${color} group-hover:scale-105 transition-transform shadow-sm`}>
        {children}
      </div>
      <span className="text-[11px] font-semibold">{label}</span>
    </button>
  );
}
